/**
 * A typed wrapper over `emitMetric` — STUDIO_DESIGN.md §6.2.
 *
 * Generic over the MAP, deliberately. There is no single `MetricEventAttrs` covering every
 * editor: that would be the shared editor core the owner ruled out, and it would mean two
 * sessions editing one interface every time either added an event. The machinery is shared;
 * each editor owns its own map and its own bound emitter (Frame picker coordination,
 * 2026-09-12) — `components/studio/state/studio-events.ts` is the first of them, and
 * `types/metric-events.edit-pdf.ts` is the hand-rolled precedent this generalises.
 *
 * Everything funnels through `utils/metric-emit.ts`, which resolves `MetricsService` from
 * the binding-free container at CALL time. Do not import `MetricsService` here: a
 * decoration-time reference to a service class is what TDZ-crashes the SSG build while
 * leaving jest green (metric-emit.ts:6-20).
 *
 * A map passed here must be a `type` alias, not an `interface`. An interface has no implicit
 * index signature, so it does not satisfy `Record<string, MetricAttrs>`; and an interface
 * written as `extends Record<string, MetricAttrs>` to get around that makes `keyof` resolve
 * to `string | number`, which collapses `E` to `never` and makes every call an error that
 * only `tsc` sees — jest stays green because babel erases all of this.
 */
import { emitMetric } from './metric-emit';
import type { MetricEventName } from '../types/metric-events';

/** What an event may carry. The transport flattens to these three primitives. */
export type MetricAttrs = Record<string, string | number | boolean>;

/**
 * Emit `event` with the attributes its map declares.
 *
 * `M` maps an event name to its attribute shape; `E` is constrained to the names that are
 * BOTH in the map and members of `MetricEventName`, so a typo in either list is a compile
 * error rather than a row in `frontend-events` nobody queries.
 */
export function emitTyped<M extends Record<string, MetricAttrs>, E extends Extract<keyof M, MetricEventName>>(
    event: E,
    attributes: M[E],
): void {
    // No cast: `E` is constrained to `MetricEventName` members already (review M6).
    emitMetric(event, attributes);
}
